import React, { useContext, useEffect, useState, useMemo } from "react";
import { injectIntl } from "react-intl";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row
} from "reactstrap";
import ReactTable from "react-table";
import classnames from "classnames";
import IntlMessages from "../../../helpers/IntlMessages";
import ShopeeV2Api from "../../../services/shopeeV2";
import { mergeShopeeCatalogFilters } from "./shopeeCatalogFilters";
import {
  getShopeeMappingStatus,
  mappingHasSyncError,
  shopeeMappingStatusLabel
} from "./shopeeMappingStatus";
import { isConcreteShopeeShopId } from "./shopeeListingEditorData";
import ShopeeWorkspaceContext from "./ShopeeWorkspaceContext";
import "react-table/react-table.css";

const hasValue = value => value !== null && value !== undefined && value !== "";

const textValue = value => {
  if (!hasValue(value)) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

const numberValue = value => {
  if (!hasValue(value)) return null;
  const normalized = Number(value);
  return Number.isFinite(normalized) ? normalized : null;
};

const formatNumber = value => {
  const normalized = numberValue(value);
  return normalized === null ? "—" : normalized.toLocaleString("vi-VN");
};

const money = value => {
  const normalized = numberValue(value);
  return normalized === null ? "—" : normalized.toLocaleString("vi-VN");
};

const formatTimestamp = value => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false
  }).format(date);
};

// SVG Channel Icons
const ChannelIcon = ({ type, size = 18 }) => {
  if (type === "shopee") {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: size,
          height: size,
          borderRadius: 4,
          backgroundColor: "#EE4D2D",
          color: "#fff",
          fontSize: 10,
          fontWeight: "bold",
          marginRight: 6
        }}
      >
        S
      </span>
    );
  }
  return null;
};

// SVG KPI Icons
const KpiIcon = ({ type }) => {
  const styles = {
    total: { bg: "#eef2ff", color: "#4f46e5" },
    mapped: { bg: "#ecfdf5", color: "#10b981" },
    unmapped: { bg: "#fff7ed", color: "#f97316" },
    conflict: { bg: "#fef2f2", color: "#ef4444" },
    stock: { bg: "#faf5ff", color: "#a855f7" },
    rate: { bg: "#ecfeff", color: "#06b6d4" }
  };
  const config = styles[type] || styles.total;

  return (
    <div
      style={{
        width: 38,
        height: 38,
        borderRadius: 8,
        backgroundColor: config.bg,
        color: config.color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 18,
        marginRight: 12,
        flexShrink: 0
      }}
    >
      {type === "total" && "🛍️"}
      {type === "mapped" && "📝"}
      {type === "unmapped" && "🎯"}
      {type === "conflict" && "⚠️"}
      {type === "stock" && "📦"}
      {type === "rate" && "📊"}
    </div>
  );
};

// Renders either a real provider value or an i18n message id fallback
const IdOrText = ({ value }) =>
  value && typeof value === "string" && value.startsWith("shopee.") ? (
    <IntlMessages id={value} />
  ) : (
    value || "—"
  );

export const normalizeShopeeMapping = mapping => {
  const source = mapping || {};
  const syncStatus = getShopeeMappingStatus(source);
  const mapped = Boolean(source.product_id);
  return {
    ...source,
    id: source.id,
    name: textValue(source.vmass_product_name),
    sku: textValue(source.vmass_sku),
    barcode: textValue(source.vmass_barcode),
    stock: numberValue(source.vmass_stock),
    price: numberValue(source.vmass_price),
    image: textValue(source.vmass_image),
    mappingStatus: mappingHasSyncError(source)
      ? "conflict"
      : mapped
        ? "mapped"
        : "unmapped",
    health: shopeeMappingStatusLabel(syncStatus),
    syncStatus,
    timeAgo: formatTimestamp(source.last_synced_at || source.updatedAt),
    shopee: {
      shopId: textValue(source.shop_id),
      itemId: textValue(source.platform_item_id),
      modelId: textValue(source.platform_model_id),
      sku: textValue(source.platform_sku),
      name: textValue(source.platform_name),
      status: shopeeMappingStatusLabel(syncStatus),
      price: numberValue(source.platform_price),
      stock: numberValue(source.platform_stock),
      image: textValue(source.platform_image)
    }
  };
};

export const getShopeeProductCardData = platformData => {
  const source = platformData || {};
  const modelId = textValue(source.modelId);
  const shopId = textValue(source.shopId) || "—";
  const itemId = textValue(source.itemId) || "—";
  const modelLabel =
    modelId === "0"
      ? "shopee.workspace.mapping.model-simple"
      : modelId || "—";
  return {
    image: textValue(source.image),
    name: textValue(source.name) || "shopee.workspace.inventory.no-name",
    sku: textValue(source.sku) || "—",
    priceLabel: `${money(source.price)} ₫`,
    stockLabel: formatNumber(source.stock),
    shopId,
    itemId,
    modelLabel,
    identityLabel: `Shop ${shopId} · Item ${itemId} · Model ${modelLabel}`,
    status: textValue(source.status) || "—"
  };
};

export const getVmassProductCardData = (productData, shopeeData) => {
  const source = productData || {};
  const shopee = shopeeData || {};
  const sku = textValue(source.sku) || "—";
  const barcode = textValue(source.barcode) || "—";
  return {
    image: textValue(source.image) || textValue(shopee.image),
    name: textValue(source.name) || null,
    sku,
    barcode,
    priceLabel: `${money(source.price)} ₫`,
    stockLabel: formatNumber(source.stock),
    identityLabel: `SKU ${sku} · Barcode ${barcode}`
  };
};

const metaCount = (value, fallback) => {
  const normalized = numberValue(value);
  return normalized === null || normalized < 0 ? fallback : normalized;
};

export const getShopeeMappingMetrics = (mappings, meta = {}) => {
  const rows = Array.isArray(mappings) ? mappings : [];
  const mapped = rows.filter(row => Boolean(row.product_id)).length;
  const unmapped = rows.length - mapped;
  const errors = rows.filter(mappingHasSyncError).length;
  const outOfStock = rows.filter(
    row => hasValue(row.platform_stock) && Number(row.platform_stock) <= 0
  ).length;
  const distinctItems = new Set(
    rows.map(row => textValue(row.platform_item_id)).filter(Boolean)
  ).size;
  return {
    totalRows: metaCount(meta.total, rows.length),
    totalItems: metaCount(meta.total_items, distinctItems),
    loadedRows: rows.length,
    mapped,
    unmapped,
    errors,
    outOfStock,
    mappingRate: rows.length ? Math.round((mapped / rows.length) * 100) : 0
  };
};

export const canCreateShopeeProduct = (row, shopId, writable) =>
  Boolean(writable && isConcreteShopeeShopId(shopId) && row && !row.product_id && hasValue(row.platform_item_id));

export const requiresVmassVariantForMapping = (mapping, candidate) =>
  Boolean(
    mapping &&
      String(mapping.platform_model_id || "0") !== "0" &&
      candidate &&
      Array.isArray(candidate.variants) &&
      candidate.variants.length
  );

export const getDefaultShopeeMappingDetail = (mappings, viewportWidth) => {
  const rows = Array.isArray(mappings) ? mappings : [];
  return Number(viewportWidth) > 1500 && rows.length ? rows[0] : null;
};

export default injectIntl(function ShopeeMappingPanel({ intl }) {
  const workspace = useContext(ShopeeWorkspaceContext) || {};
  const workspaceMappings = workspace.data && workspace.data.mappings;
  const rawMappings = useMemo(
    () => (Array.isArray(workspaceMappings) ? workspaceMappings : []),
    [workspaceMappings]
  );

  // Active States
  const [searchQuery, setSearchQuery] = useState("");
  const [mappingFilter, setMappingFilter] = useState("all");
  const [syncFilter, setSyncFilter] = useState("all");

  const [selectedRows, setSelectedRows] = useState({});
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [detailTab, setDetailTab] = useState("info"); // 'info' | 'mapping' | 'history'
  const [mappingDialog, setMappingDialog] = useState(null);
  const [productId, setProductId] = useState("");
  const [variantId, setVariantId] = useState("");
  const [candidateSearch, setCandidateSearch] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);

  const mappingList = useMemo(
    () => rawMappings.map(normalizeShopeeMapping).filter(item => hasValue(item.id)),
    [rawMappings]
  );
  const filteredData = mappingList;
  const metrics = useMemo(
    () => getShopeeMappingMetrics(rawMappings, workspace.meta || {}),
    [rawMappings, workspace.meta]
  );
  const setWorkspaceFilters = workspace.setFilters;

  useEffect(() => {
    if (typeof setWorkspaceFilters !== "function") return undefined;
    const timer = setTimeout(() => {
      setWorkspaceFilters(current =>
        mergeShopeeCatalogFilters(current, {
          search: searchQuery,
          mappingState: mappingFilter,
          syncState: syncFilter
        })
      );
    }, 250);
    return () => clearTimeout(timer);
  }, [mappingFilter, searchQuery, setWorkspaceFilters, syncFilter]);

  useEffect(() => {
    const visibleIds = new Set(mappingList.map(item => String(item.id)));
    setSelectedRows(current => {
      const next = Object.keys(current).reduce((result, id) => {
        if (current[id] && visibleIds.has(String(id))) result[id] = true;
        return result;
      }, {});
      return Object.keys(next).length === Object.keys(current).length
        ? current
        : next;
    });
    setSelectedProduct(current =>
      current
        ? mappingList.find(item => String(item.id) === String(current.id)) || null
        : null
    );
  }, [mappingList]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const defaultDetailProduct = getDefaultShopeeMappingDetail(
      mappingList,
      window.innerWidth
    );
    if (!defaultDetailProduct) return;
    setSelectedProduct(current => current || defaultDetailProduct);
  }, [mappingList]);

  const selectedMappingIds = mappingList
    .filter(item => selectedRows[item.id])
    .map(item => item.id);
  const catalogWritable = Boolean(
    workspace.capabilities &&
      workspace.capabilities.shopee_catalog &&
      workspace.capabilities.shopee_catalog.write
  );
  const inventoryWritable = Boolean(
    workspace.capabilities &&
      workspace.capabilities.shopee_inventory &&
      workspace.capabilities.shopee_inventory.write
  );
  const hasConcreteShop = isConcreteShopeeShopId(workspace.selectedShopId);
  const busy = workspace.busy || {};
  const page = Math.max(
    1,
    Number(
      (workspace.meta && workspace.meta.page) ||
        (workspace.filters && workspace.filters.page) ||
        1
    )
  );
  const pageSize = Math.max(
    1,
    Number(
      (workspace.meta && workspace.meta.page_size) ||
        (workspace.filters && workspace.filters.page_size) ||
        20
    )
  );
  const totalPages = Math.max(
    1,
    Number((workspace.meta && workspace.meta.total_pages) || 1)
  );
  const pageStart = mappingList.length ? (page - 1) * pageSize + 1 : 0;
  const pageEnd = mappingList.length ? pageStart + mappingList.length - 1 : 0;

  const openMapping = row => {
    setMappingDialog(row);
    setProductId(row.product_id || "");
    setVariantId(row.variant_id || "");
    setCandidateSearch("");
  };

  useEffect(() => {
    if (!mappingDialog) return undefined;
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setCandidatesLoading(true);
      try {
        const response = await ShopeeV2Api.catalogCandidates(
          { search: candidateSearch, page_size: 100 },
          controller.signal
        );
        setCandidates((response && response.data) || []);
      } catch (error) {
        if (error.name !== "CanceledError" && error.code !== "ERR_CANCELED") setCandidates([]);
      } finally {
        if (!controller.signal.aborted) setCandidatesLoading(false);
      }
    }, 250);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [mappingDialog, candidateSearch]);

  const selectedCandidate = candidates.find(candidate => String(candidate.id) === String(productId));
  const availableVariants =
    selectedCandidate && Array.isArray(selectedCandidate.variants)
      ? selectedCandidate.variants
      : [];
  const requiresVariant = requiresVmassVariantForMapping(
    mappingDialog,
    selectedCandidate
  );

  const saveMapping = () => {
    if (!mappingDialog || !Number(productId) || (requiresVariant && !Number(variantId))) return;
    workspace.runAction(
      `mapping-${mappingDialog.id}`,
      key => ShopeeV2Api.updateMapping(
        mappingDialog.id,
        { product_id: Number(productId), variant_id: variantId ? Number(variantId) : null },
        key
      ),
      intl.formatMessage({ id: "shopee.workspace.catalog.mapping-saved" })
    );
    setMappingDialog(null);
  };

  const clearMapping = () => {
    if (!mappingDialog) return;
    workspace.runAction(
      `mapping-${mappingDialog.id}`,
      key => ShopeeV2Api.updateMapping(mappingDialog.id, { product_id: null, variant_id: null }, key),
      intl.formatMessage({ id: "shopee.workspace.catalog.mapping-cleared" })
    );
    setMappingDialog(null);
  };

  const createProductFromShopee = row => {
    if (!canCreateShopeeProduct(row, workspace.selectedShopId, catalogWritable)) return;
    workspace.runAction(
      `draft-${row.platform_item_id}`,
      key => ShopeeV2Api.createCatalogDraft(workspace.selectedShopId, row.platform_item_id, null, key),
      intl.formatMessage({ id: "shopee.workspace.mapping.draft-created-from-listing" })
    );
  };

  // Resolves i18n message ids for plain-text attributes; passes real values through
  const msgOrText = value =>
    value && typeof value === "string" && value.startsWith("shopee.")
      ? intl.formatMessage({ id: value })
      : value;

  // Row Selection logic
  const handleSelectAll = e => {
    if (e.target.checked) {
      const allSelected = {};
      filteredData.forEach(item => {
        allSelected[item.id] = true;
      });
      setSelectedRows(allSelected);
    } else {
      setSelectedRows({});
    }
  };

  const handleSelectRow = (id, checked) => {
    setSelectedRows(prev => ({
      ...prev,
      [id]: checked
    }));
  };

  // Helper mapping platform cell renderer
  const renderPlatformCell = (platformData, row) => {
    if (!platformData || !platformData.itemId) {
      return (
        <div
          onClick={() => setSelectedProduct(row)}
          style={{
            border: "1px dashed #cbd5e1",
            borderRadius: 6,
            padding: "8px 12px",
            backgroundColor: "#f8fafc",
            cursor: "pointer",
            textAlign: "center",
            color: "#64748b",
            fontSize: 12,
            fontWeight: 500,
            transition: "all 0.2s"
          }}
          className="hover-opacity"
        >
          <IntlMessages id="shopee.workspace.mapping.no-provider-data" />
        </div>
      );
    }

    const isConflict = row.syncStatus === "error";
    const card = getShopeeProductCardData(platformData);
    return (
      <div
        onClick={() => setSelectedProduct(row)}
        style={{
          border: isConflict ? "1px solid #fca5a5" : "1px solid #e2e8f0",
          borderRadius: 5,
          padding: "5px 7px",
          backgroundColor: isConflict ? "#fef2f2" : "#ffffff",
          cursor: "pointer"
        }}
      >
        <div className="d-flex align-items-center" style={{ gap: 8 }}>
          <div
            style={{
              position: "relative",
              width: 38,
              height: 38,
              borderRadius: 5,
              overflow: "hidden",
              border: "1px solid #e2e8f0",
              backgroundColor: "#fff7ed",
              color: "#f97316",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 9,
              fontWeight: 700,
              flexShrink: 0
            }}
          >
            SHP
            {card.image ? (
              <img
                src={card.image}
                alt={card.name}
                loading="lazy"
                onError={event => {
                  event.currentTarget.style.display = "none";
                }}
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover"
                }}
              />
            ) : null}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="d-flex align-items-center" style={{ gap: 5 }}>
              <div
                title={msgOrText(card.name)}
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#0f172a",
                  lineHeight: 1.25,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis"
                }}
              >
                <IdOrText value={card.name} />
              </div>
              <span
                title={msgOrText(card.status)}
                style={{
                  maxWidth: 86,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: 9,
                  lineHeight: 1.3,
                  padding: "1px 5px",
                  borderRadius: 3,
                  backgroundColor: isConflict ? "#fee2e2" : "#dcfce7",
                  color: isConflict ? "#dc2626" : "#166534",
                  fontWeight: 600,
                  flexShrink: 0
                }}
              >
                <IdOrText value={card.status} />
              </span>
            </div>
            <div
              title={intl.formatMessage(
                { id: "shopee.workspace.mapping.tooltip-full" },
                { sku: card.sku, price: card.priceLabel, stock: card.stockLabel }
              )}
              style={{
                fontSize: 10,
                lineHeight: 1.35,
                color: "#64748b",
                marginTop: 2,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}
            >
              <code>{card.sku}</code> · {card.priceLabel} ·{" "}
              <span
                style={{
                  color:
                    platformData.stock === null ? "#64748b" : platformData.stock > 0 ? "#047857" : "#b91c1c",
                  fontWeight: 600
                }}
              >
                <IntlMessages id="shopee.workspace.mapping.stock-colon" />{" "}
                {card.stockLabel}
              </span>
            </div>
            <div
              title={card.identityLabel}
              style={{
                fontSize: 9,
                lineHeight: 1.3,
                color: "#94a3b8",
                marginTop: 1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}
            >
              {card.identityLabel}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Columns for ReactTable
  const columns = [
    {
      Header: () => (
        <Input
          type="checkbox"
          style={{ position: "static", margin: 0 }}
          onChange={handleSelectAll}
          checked={
            filteredData.length > 0 &&
            filteredData.every(item => selectedRows[item.id])
          }
        />
      ),
      id: "select",
      accessor: d => d.id,
      width: 42,
      sortable: false,
      Cell: row => (
        <div className="text-center">
          <Input
            type="checkbox"
            style={{ position: "static", margin: 0 }}
            checked={Boolean(selectedRows[row.value])}
            onChange={e => handleSelectRow(row.value, e.target.checked)}
          />
        </div>
      )
    },
    {
      Header: <IntlMessages id="shopee.workspace.mapping.col.vmass-product" />,
      accessor: "name",
      minWidth: 220,
      Cell: row => {
        const item = row.original;
        if (!item.product_id) {
          const chooseTitle = intl.formatMessage({
            id: catalogWritable
              ? "shopee.workspace.mapping.pick-product-aria"
              : "shopee.workspace.mapping.no-mapping-permission"
          });
          return (
            <div className="d-flex align-items-center justify-content-center h-100">
              <Button
                color="primary"
                size="sm"
                className="py-1 px-2"
                title={chooseTitle}
                aria-label={chooseTitle}
                disabled={!catalogWritable}
                style={{ fontSize: 11, lineHeight: 1.35, borderRadius: 4 }}
                onClick={() => openMapping(item)}
              >
                <IntlMessages id="shopee.workspace.common.pick-product" />
              </Button>
            </div>
          );
        }

        const card = getVmassProductCardData(item, item.shopee);
        return (
          <div
            className="d-flex align-items-center cursor-pointer"
            onClick={() => setSelectedProduct(item)}
            style={{ cursor: "pointer", gap: 8, width: "100%" }}
          >
            <div
              style={{
                position: "relative",
                width: 38,
                height: 38,
                borderRadius: 5,
                overflow: "hidden",
                border: "1px solid #e2e8f0",
                backgroundColor: "#f8fafc",
                color: "#64748b",
                display: "flex",
                alignItems: "center",
                // justifyContent: "center",
                fontSize: 9,
                fontWeight: 700,
                flexShrink: 0
              }}
            >
              VM
              {card.image ? (
                <img
                  src={card.image}
                  alt={card.name}
                  loading="lazy"
                  onError={event => {
                    event.currentTarget.style.display = "none";
                  }}
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover"
                  }}
                />
              ) : null}
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="d-flex align-items-center" style={{ gap: 4 }}>
                <div
                  title={msgOrText(card.name)}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    fontWeight: 700,
                    color: "#1e293b",
                    fontSize: 11,
                    lineHeight: 1.25,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}
                >
                  <IdOrText value={card.name} />
                </div>
              </div>
              <div
                title={card.identityLabel}
                style={{
                  fontSize: 9,
                  lineHeight: 1.3,
                  color: "#94a3b8",
                  marginTop: 1,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis"
                }}
              >
                {card.identityLabel}
              </div>
              <div
                title={intl.formatMessage(
                  { id: "shopee.workspace.mapping.tooltip-price-stock" },
                  { price: card.priceLabel, stock: card.stockLabel }
                )}
                style={{
                  fontSize: 10,
                  lineHeight: 1.35,
                  color: "#64748b",
                  marginTop: 1,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis"
                }}
              >
                <IntlMessages id="shopee.workspace.mapping.price-label" />{" "}
                {card.priceLabel} ·{" "}
                <span
                  style={{
                    color:
                      item.stock === null
                        ? "#64748b"
                        : item.stock > 0
                          ? "#0369a1"
                          : "#b91c1c",
                    fontWeight: 600
                  }}
                >
                  <IntlMessages id="shopee.workspace.mapping.stock-colon" />{" "}
                  {card.stockLabel}
                </span>
              </div>
            </div>
          </div>
        );
      }
    },
    {
      Header: <IntlMessages id="shopee.workspace.mapping.col.mapping" />,
      accessor: "mappingStatus",
      minWidth: 140,
      Cell: row => {
        const item = row.original;
        if (item.mappingStatus === "mapped") {
          return (
            <div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#16a34a",
                  display: "inline-flex",
                  alignItems: "center"
                }}
              >
                <span style={{ marginRight: 4 }}>✔</span>{" "}
                <IntlMessages id="shopee.workspace.catalog.mapping-mapped" />
              </div>
              <div style={{ fontSize: 11, color: "#16a34a" }}>
                <IdOrText value={item.health} />
              </div>
              {item.timeAgo && (
                <div style={{ fontSize: 10, color: "#94a3b8" }}>
                  {item.timeAgo}
                </div>
              )}
            </div>
          );
        }
        if (item.mappingStatus === "conflict") {
          return (
            <div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#dc2626",
                  display: "inline-flex",
                  alignItems: "center"
                }}
              >
                <span style={{ marginRight: 4 }}>⚠️</span>{" "}
                <IntlMessages id="shopee.workspace.mapping.state-conflict" />
              </div>
              <div style={{ fontSize: 11, color: "#dc2626" }}>
                <IdOrText value={item.health} />
              </div>
              {item.timeAgo && (
                <div style={{ fontSize: 10, color: "#94a3b8" }}>
                  {item.timeAgo}
                </div>
              )}
            </div>
          );
        }
        return (
          <div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: "#ea580c",
                display: "inline-flex",
                alignItems: "center"
              }}
            >
              <span style={{ marginRight: 4 }}>⚠️</span>{" "}
              <IntlMessages id="shopee.workspace.catalog.mapping-unmapped" />
            </div>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>
              <IdOrText value={item.health} />
            </div>
          </div>
        );
      }
    },
    {
      Header: () => (
        <div className="d-flex align-items-center">
          <ChannelIcon type="shopee" /> Shopee
        </div>
      ),
      accessor: "shopee",
      minWidth: 260,
      Cell: row => renderPlatformCell(row.value, row.original)
    }
  ];

  const runAutoMap = ids => {
    const mappingIds = (Array.isArray(ids) ? ids : []).slice(0, 100);
    if (
      !mappingIds.length ||
      !hasConcreteShop ||
      !catalogWritable ||
      typeof workspace.runAction !== "function"
    ) {
      return;
    }
    workspace.runAction(
      `auto-map-${mappingIds.join("-")}`,
      key =>
        ShopeeV2Api.remediateIssues(
          workspace.selectedShopId,
          "auto_map_sku",
          mappingIds,
          key
        ),
      intl.formatMessage(
        { id: "shopee.workspace.mapping.auto-map-launched" },
        { count: mappingIds.length }
      )
    );
  };

  const syncCatalog = () => {
    if (
      !workspace.selectedShopId ||
      !catalogWritable ||
      typeof workspace.runAction !== "function"
    ) {
      return;
    }
    workspace.runAction(
      "import-catalog",
      key => ShopeeV2Api.importCatalog(workspace.selectedShopId, null, key),
      intl.formatMessage({ id: "shopee.workspace.catalog.import-queued" })
    );
  };

  const updatePage = nextPage => {
    if (typeof setWorkspaceFilters !== "function") return;
    setWorkspaceFilters({
      ...(workspace.filters || {}),
      page: Math.min(totalPages, Math.max(1, nextPage))
    });
  };

  const updatePageSize = value => {
    if (typeof setWorkspaceFilters !== "function") return;
    setWorkspaceFilters({
      ...(workspace.filters || {}),
      page: 1,
      page_size: Number(value)
    });
  };

  const unmapSelectedProduct = () => {
    if (
      !selectedProduct ||
      !selectedProduct.product_id ||
      !catalogWritable ||
      typeof workspace.runAction !== "function"
    ) {
      return;
    }
    workspace.runAction(
      `mapping-${selectedProduct.id}`,
      key =>
        ShopeeV2Api.updateMapping(
          selectedProduct.id,
          { product_id: null, variant_id: null },
          key
        ),
      intl.formatMessage({ id: "shopee.workspace.catalog.mapping-cleared" })
    );
  };

  const syncSelectedInventory = () => {
    if (
      !selectedProduct ||
      !selectedProduct.product_id ||
      !isConcreteShopeeShopId(selectedProduct.shopee.shopId) ||
      !inventoryWritable ||
      typeof workspace.runAction !== "function"
    ) {
      return;
    }
    workspace.runAction(
      `sync-inventory-${selectedProduct.id}`,
      key =>
        ShopeeV2Api.syncInventory(
          selectedProduct.shopee.shopId,
          [
            {
              id: selectedProduct.id,
              platform_item_id: selectedProduct.shopee.itemId,
              platform_model_id: selectedProduct.shopee.modelId
            }
          ],
          key
        ),
      intl.formatMessage({ id: "shopee.workspace.mapping.inventory-sync-queued" })
    );
  };

  return (
    <div style={{ backgroundColor: "#f8fafc", minHeight: "100vh", padding: "8px 0" }}>
      {workspace.error ? (
        <Alert color="danger" className="d-flex justify-content-between align-items-center">
          <span>{workspace.error}</span>
          {typeof workspace.load === "function" ? (
            <Button color="danger" outline size="sm" onClick={workspace.load}>
              <IntlMessages id="shopee.workspace.common.retry" />
            </Button>
          ) : null}
        </Alert>
      ) : null}
      {!workspace.selectedShopId && !workspace.loading ? (
        <Alert color="info">
          <IntlMessages id="shopee.workspace.mapping.no-shop-selected" />
        </Alert>
      ) : null}
      {/* 1. KPI Cards Header (6 Cards) */}
      <Row className="mb-3">
        <Col xl="2" lg="4" sm="6" className="mb-2 mb-xl-0">
          <Card className="h-100 border-0 shadow-sm">
            <CardBody className="p-3 d-flex align-items-center">
              <KpiIcon type="total" />
              <div>
                <div style={{ fontSize: 12, color: "#64748b" }}><IntlMessages id="shopee.workspace.catalog.kpi-total-listings" /></div>
                <div style={{ fontSize: 20, fontWeight: 700, color: "#0f172a" }}>
                  {formatNumber(metrics.totalItems)}
                </div>
                <div style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>
                  <IntlMessages
                    id="shopee.workspace.mapping.kpi.total-sub"
                    values={{ count: formatNumber(metrics.totalRows) }}
                  />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col xl="2" lg="4" sm="6" className="mb-2 mb-xl-0">
          <Card className="h-100 border-0 shadow-sm">
            <CardBody className="p-3 d-flex align-items-center">
              <KpiIcon type="mapped" />
              <div>
                <div style={{ fontSize: 12, color: "#64748b" }}><IntlMessages id="shopee.workspace.catalog.mapping-mapped" /></div>
                <div style={{ fontSize: 20, fontWeight: 700, color: "#0f172a" }}>
                  {formatNumber(metrics.mapped)}
                </div>
                <div style={{ fontSize: 11, color: "#64748b" }}>
                  <IntlMessages
                    id="shopee.workspace.mapping.kpi.rate-page"
                    values={{ percent: metrics.mappingRate }}
                  />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col xl="2" lg="4" sm="6" className="mb-2 mb-xl-0">
          <Card className="h-100 border-0 shadow-sm">
            <CardBody className="p-3 d-flex align-items-center">
              <KpiIcon type="unmapped" />
              <div>
                <div style={{ fontSize: 12, color: "#64748b" }}><IntlMessages id="shopee.workspace.catalog.mapping-unmapped" /></div>
                <div style={{ fontSize: 20, fontWeight: 700, color: "#0f172a" }}>
                  {formatNumber(metrics.unmapped)}
                </div>
                <div style={{ fontSize: 11, color: "#ea580c" }}><IntlMessages id="shopee.workspace.inventory.sub.on-page" /></div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col xl="2" lg="4" sm="6" className="mb-2 mb-xl-0">
          <Card className="h-100 border-0 shadow-sm">
            <CardBody className="p-3 d-flex align-items-center">
              <KpiIcon type="conflict" />
              <div>
                <div style={{ fontSize: 12, color: "#64748b" }}><IntlMessages id="shopee.workspace.catalog.kpi-sync-errors" /></div>
                <div style={{ fontSize: 20, fontWeight: 700, color: "#ef4444" }}>
                  {formatNumber(metrics.errors)}
                </div>
                <div style={{ fontSize: 11, color: "#dc2626" }}>
                  <IntlMessages id="shopee.workspace.inventory.sub.on-page" />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col xl="2" lg="4" sm="6" className="mb-2 mb-xl-0">
          <Card className="h-100 border-0 shadow-sm">
            <CardBody className="p-3 d-flex align-items-center">
              <KpiIcon type="stock" />
              <div>
                <div style={{ fontSize: 12, color: "#64748b" }}><IntlMessages id="shopee.workspace.inventory.status.out-of-stock" /></div>
                <div style={{ fontSize: 20, fontWeight: 700, color: "#a855f7" }}>
                  {formatNumber(metrics.outOfStock)}
                </div>
                <div style={{ fontSize: 11, color: "#7c3aed" }}>
                  <IntlMessages id="shopee.workspace.inventory.sub.on-page" />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col xl="2" lg="4" sm="6" className="mb-2 mb-xl-0">
          <Card className="h-100 border-0 shadow-sm">
            <CardBody className="p-3 d-flex align-items-center">
              <KpiIcon type="rate" />
              <div>
                <div style={{ fontSize: 12, color: "#64748b" }}>
                  <IntlMessages id="shopee.workspace.mapping.kpi.rate" />
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, color: "#0f172a" }}>
                  {metrics.mappingRate}%
                </div>
                <div style={{ fontSize: 11, color: "#16a34a" }}>
                  <IntlMessages id="shopee.workspace.inventory.sub.on-page" />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* 2. Filter Toolbar */}
      <Card className="border-0 shadow-sm mb-3">
        <CardBody className="p-3">
          <div className="d-flex flex-wrap align-items-center justify-content-between" style={{ gap: 10 }}>
            {/* Search Input & Select Controls */}
            <div className="d-flex flex-wrap align-items-center" style={{ gap: 8, flex: 1 }}>
              <div style={{ minWidth: 220, position: "relative" }}>
                <Input
                  type="text"
                  placeholder={intl.formatMessage({
                    id: "shopee.workspace.mapping.search-placeholder"
                  })}
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  bsSize="sm"
                  style={{
                    borderRadius: 6,
                    paddingLeft: 30,
                    borderColor: "#cbd5e1"
                  }}
                />
                <span
                  style={{
                    position: "absolute",
                    left: 10,
                    top: 6,
                    color: "#94a3b8",
                    fontSize: 12
                  }}
                >
                  🔍
                </span>
              </div>

              <Input
                type="select"
                bsSize="sm"
                value={mappingFilter}
                onChange={e => setMappingFilter(e.target.value)}
                style={{ width: "auto", borderRadius: 6, borderColor: "#cbd5e1" }}
              >
                <option value="all">{intl.formatMessage({ id: "shopee.workspace.catalog.mapping-all" })}</option>
                <option value="mapped">{intl.formatMessage({ id: "shopee.workspace.catalog.mapping-mapped" })}</option>
                <option value="unmapped">{intl.formatMessage({ id: "shopee.workspace.catalog.mapping-unmapped" })}</option>
              </Input>

              <Input
                type="select"
                bsSize="sm"
                value={syncFilter}
                onChange={e => setSyncFilter(e.target.value)}
                style={{ width: "auto", borderRadius: 6, borderColor: "#cbd5e1" }}
              >
                <option value="all">{intl.formatMessage({ id: "shopee.workspace.catalog.sync-state-all" })}</option>
                <option value="healthy">Healthy</option>
                <option value="error">{intl.formatMessage({ id: "shopee.workspace.catalog.sync-state-error" })}</option>
              </Input>
            </div>

            {/* Action Buttons */}
            <div className="d-flex align-items-center" style={{ gap: 8 }}>
              <Button
                color="outline-primary"
                size="sm"
                style={{ borderRadius: 6, fontWeight: 500 }}
                disabled={
                  !catalogWritable ||
                  !hasConcreteShop ||
                  !selectedMappingIds.length ||
                  selectedMappingIds.length > 100 ||
                  busy[`auto-map-${selectedMappingIds.join("-")}`]
                }
                onClick={() => runAutoMap(selectedMappingIds)}
              >
                <IntlMessages
                  id="shopee.workspace.mapping.auto-map-selected"
                  values={{ count: selectedMappingIds.length }}
                />
              </Button>
              <Button
                color="primary"
                size="sm"
                style={{ borderRadius: 6, fontWeight: 600 }}
                disabled={
                  !catalogWritable ||
                  !workspace.selectedShopId ||
                  busy["import-catalog"]
                }
                onClick={syncCatalog}
              >
                <IntlMessages id="shopee.workspace.catalog.sync-shopee-catalog" />
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* 3. Main Data Card & Right Detail Drawer Container */}
      <div className="d-flex" style={{ gap: 16 }}>
        {/* Left Side Table Container */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <Card className="border-0 shadow-sm mb-3">
            <CardBody className="p-3">
              <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                <div>
                  <h6 className="mb-0" style={{ fontWeight: 700 }}>
                    <IntlMessages id="shopee.workspace.mapping.title" />
                  </h6>
                  <small className="text-muted">
                    <IntlMessages
                      id="shopee.workspace.mapping.data-scope-banner"
                      values={{
                        shop:
                          workspace.selectedShopId ||
                          intl.formatMessage({
                            id: "shopee.workspace.mapping.shop-unselected"
                          })
                      }}
                    />
                  </small>
                </div>
              </div>

              {/* Table Render using ReactTable */}
              <div className="shopee-mapping-table-wrapper">
                <ReactTable
                  data={filteredData}
                  columns={columns}
                  defaultPageSize={pageSize}
                  pageSize={filteredData.length || pageSize}
                  showPagination={false}
                  minRows={0}
                  className="-highlight"
                  NoDataComponent={() => (
                    <div className="text-center py-5 text-muted">
                      {workspace.loading
                        ? intl.formatMessage({ id: "shopee.workspace.mapping.loading" })
                        : intl.formatMessage({ id: "shopee.workspace.mapping.empty" })}
                    </div>
                  )}
                  getTableProps={() => ({
                    style: {
                      border: "none"
                    }
                  })}
                  getTrProps={(state, rowInfo) => {
                    const isSelected = rowInfo && selectedProduct && rowInfo.original.id === selectedProduct.id;
                    return {
                      style: {
                        backgroundColor: isSelected ? "#eff6ff" : "transparent",
                        cursor: rowInfo ? "pointer" : "default"
                      },
                      onClick: () => {
                        if (rowInfo) setSelectedProduct(rowInfo.original);
                      }
                    };
                  }}
                />
              </div>

              {/* Custom Footer Pagination Bar */}
              <div className="d-flex flex-wrap justify-content-between align-items-center mt-3 pt-3 border-top">
                <div style={{ fontSize: 12, color: "#64748b" }}>
                  <IntlMessages
                    id="shopee.workspace.inventory.pagination-summary"
                    values={{
                      from: formatNumber(pageStart),
                      to: formatNumber(pageEnd),
                      total: formatNumber(metrics.totalRows)
                    }}
                  />
                </div>
                <div className="d-flex align-items-center" style={{ gap: 4 }}>
                  <Button size="sm" color="light" disabled={page <= 1} onClick={() => updatePage(page - 1)} style={{ borderRadius: 4 }}>
                    ‹
                  </Button>
                  <Button size="sm" color="primary" disabled style={{ borderRadius: 4, fontWeight: 600 }}>
                    {page}
                  </Button>
                  {page < totalPages ? (
                    <Button size="sm" color="light" onClick={() => updatePage(page + 1)} style={{ borderRadius: 4 }}>
                      {page + 1}
                    </Button>
                  ) : null}
                  {page + 1 < totalPages ? <span className="px-1 text-muted">…</span> : null}
                  {page + 1 < totalPages ? (
                    <Button size="sm" color="light" onClick={() => updatePage(totalPages)} style={{ borderRadius: 4 }}>
                      {totalPages}
                    </Button>
                  ) : null}
                  <Button size="sm" color="light" disabled={page >= totalPages} onClick={() => updatePage(page + 1)} style={{ borderRadius: 4 }}>
                    ›
                  </Button>
                </div>
                <Input
                  type="select"
                  bsSize="sm"
                  value={pageSize}
                  onChange={event => updatePageSize(event.target.value)}
                  style={{ width: "auto", borderRadius: 6, fontSize: 12 }}
                >
                  {[20, 50, 100].map(size => (
                    <option value={size} key={size}>{size} / trang</option>
                  ))}
                </Input>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Right Side Detail Drawer Panel ("Chi tiết mapping") */}
        {selectedProduct && (
          <div
            style={{
              width: 320,
              flexShrink: 0,
              display: "flex",
              flexDirection: "column"
            }}
          >
            <Card className="border-0 shadow-sm h-100">
              <CardBody className="p-3 d-flex flex-column" style={{ height: "100%" }}>
                {/* Header of Drawer */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0" style={{ fontWeight: 700, color: "#0f172a" }}>
                    <IntlMessages id="shopee.workspace.mapping.detail-title" />
                  </h6>
                  <button
                    type="button"
                    className="close"
                    onClick={() => setSelectedProduct(null)}
                    style={{ outline: "none" }}
                  >
                    ×
                  </button>
                </div>

                {/* Product Header Card */}
                <div
                  className="d-flex align-items-center mb-3 p-2"
                  style={{ backgroundColor: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0" }}
                >
                  {selectedProduct.shopee.image || selectedProduct.image ? (
                    <img
                      src={selectedProduct.shopee.image || selectedProduct.image}
                      alt={selectedProduct.shopee.name || selectedProduct.name || ""}
                      style={{
                        width: 50,
                        height: 50,
                        borderRadius: 6,
                        objectFit: "cover",
                        marginRight: 10,
                        border: "1px solid #cbd5e1"
                      }}
                    />
                  ) : null}
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: "#0f172a" }}>
                      <IdOrText
                        value={
                          selectedProduct.shopee.name || selectedProduct.name
                        }
                      />
                    </div>
                    <div style={{ fontSize: 11, color: "#64748b" }}>
                      SKU Shopee: {selectedProduct.shopee.sku || "—"}
                    </div>
                    <div style={{ fontSize: 10, color: "#94a3b8" }}>
                      Item: {selectedProduct.shopee.itemId || "—"} · Model: {selectedProduct.shopee.modelId || "—"}
                    </div>
                  </div>
                  <div className="ml-auto">
                    <Badge
                      color={
                        selectedProduct.syncStatus === "healthy"
                          ? "success"
                          : selectedProduct.syncStatus === "error"
                            ? "danger"
                            : "warning"
                      }
                    >
                      <IdOrText value={selectedProduct.health} />
                    </Badge>
                  </div>
                </div>

                {/* Drawer Nav Tabs */}
                <Nav tabs className="mb-3" style={{ fontSize: 12 }}>
                  {["info", "mapping", "history"].map(tabKey => (
                    <NavItem key={tabKey} style={{ flex: 1, textAlign: "center" }}>
                      <NavLink
                        className={classnames({ active: detailTab === tabKey })}
                        style={{
                          cursor: "pointer",
                          padding: "6px 0",
                          fontWeight: detailTab === tabKey ? 600 : 400,
                          color: detailTab === tabKey ? "#2563eb" : "#64748b"
                        }}
                        onClick={() => setDetailTab(tabKey)}
                      >
                        {tabKey === "info" && <IntlMessages id="shopee.workspace.mapping.tab-info" />}
                        {tabKey === "mapping" && <IntlMessages id="shopee.workspace.mapping.col.mapping" />}
                        {tabKey === "history" && <IntlMessages id="shopee.workspace.mapping.tab-history" />}
                      </NavLink>
                    </NavItem>
                  ))}
                </Nav>

                {/* Tab Content */}
                <div style={{ flex: 1, overflowY: "auto" }}>
                  {detailTab === "info" && (
                    <div>
                      <div className="mb-3">
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#334155", marginBottom: 8 }}>
                          <IntlMessages id="shopee.workspace.mapping.section-vmass" />
                        </div>
                        <div className="d-flex justify-content-between py-1" style={{ fontSize: 12, borderBottom: "1px dashed #f1f5f9" }}>
                          <span style={{ color: "#64748b" }}><IntlMessages id="shopee.workspace.common.product-name" /></span>
                          <span style={{ fontWeight: 600, color: "#0f172a" }}><IdOrText value={selectedProduct.name} /></span>
                        </div>
                        <div className="d-flex justify-content-between py-1" style={{ fontSize: 12, borderBottom: "1px dashed #f1f5f9" }}>
                          <span style={{ color: "#64748b" }}>SKU</span>
                          <span style={{ fontWeight: 600, color: "#0f172a" }}>{selectedProduct.sku || "—"}</span>
                        </div>
                        <div className="d-flex justify-content-between py-1" style={{ fontSize: 12, borderBottom: "1px dashed #f1f5f9" }}>
                          <span style={{ color: "#64748b" }}>Barcode</span>
                          <span style={{ color: "#0f172a" }}>{selectedProduct.barcode || "—"}</span>
                        </div>
                        <div className="d-flex justify-content-between py-1" style={{ fontSize: 12, borderBottom: "1px dashed #f1f5f9" }}>
                          <span style={{ color: "#64748b" }}><IntlMessages id="shopee.workspace.mapping.available-stock" /></span>
                          <span style={{ color: "#0f172a" }}>{formatNumber(selectedProduct.stock)}</span>
                        </div>
                        <div className="d-flex justify-content-between py-1" style={{ fontSize: 12, borderBottom: "1px dashed #f1f5f9" }}>
                          <span style={{ color: "#64748b" }}><IntlMessages id="shopee.workspace.catalog.col-price" /></span>
                          <span style={{ fontWeight: 600, color: "#2563eb" }}>{money(selectedProduct.price)} ₫</span>
                        </div>
                      </div>

                      <div className="mb-3">
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#334155", marginBottom: 8 }}>
                          <IntlMessages id="shopee.workspace.mapping.section-shopee" />
                        </div>

                        {/* Shopee Channel Box */}
                        <div
                          className="p-2 mb-2"
                          style={{
                            border: "1px solid #e2e8f0",
                            borderRadius: 6,
                            backgroundColor: "#ffffff"
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <div className="d-flex align-items-center" style={{ fontSize: 12, fontWeight: 700 }}>
                              <ChannelIcon type="shopee" /> Shopee
                            </div>
                            <Badge
                              color={
                                selectedProduct.syncStatus === "healthy"
                                  ? "success"
                                  : selectedProduct.syncStatus === "error"
                                    ? "danger"
                                    : "warning"
                              }
                            >
                              <IdOrText value={selectedProduct.health} />
                            </Badge>
                          </div>
                          {selectedProduct.shopee ? (
                            <div style={{ fontSize: 11, color: "#475569" }}>
                              <div>Shop ID: <strong>{selectedProduct.shopee.shopId || "—"}</strong></div>
                              <div>Item ID: {selectedProduct.shopee.itemId || "—"}</div>
                              <div>Model ID: {selectedProduct.shopee.modelId || "—"}</div>
                              <div>SKU Shopee: <strong>{selectedProduct.shopee.sku || "—"}</strong></div>
                              <div><IntlMessages id="shopee.workspace.mapping.shopee-name-colon" /> <IdOrText value={selectedProduct.shopee.name} /></div>
                              <div><IntlMessages id="shopee.workspace.catalog.col-price" />: {money(selectedProduct.shopee.price)} ₫</div>
                              <div><IntlMessages id="shopee.workspace.catalog.col-stock" />: {formatNumber(selectedProduct.shopee.stock)}</div>
                            </div>
                          ) : (
                            <div className="text-muted" style={{ fontSize: 11 }}><IntlMessages id="shopee.workspace.mapping.not-linked" /></div>
                          )}
                        </div>

                      </div>
                    </div>
                  )}

                  {detailTab === "mapping" && (
                    <div style={{ fontSize: 12, color: "#475569" }}>
                      <div className="mb-1">Product ID VMASS: <strong>{selectedProduct.product_id || "—"}</strong></div>
                      <div className="mb-1">Variant ID VMASS: <strong>{selectedProduct.variant_id || "—"}</strong></div>
                      <div className="mb-1">Conflict state: <strong>{selectedProduct.conflict_state || "none"}</strong></div>
                      <div className="mb-2"><IntlMessages id="shopee.workspace.mapping.sync-enabled-colon" /> <strong>{selectedProduct.sync_enabled === false ? <IntlMessages id="shopee.workspace.common.no" /> : <IntlMessages id="shopee.workspace.common.yes" />}</strong></div>
                      {selectedProduct.last_sync_error ? (
                        <Alert color="danger" className="py-2">
                          {selectedProduct.last_sync_error}
                        </Alert>
                      ) : null}
                      {!selectedProduct.product_id ? (
                        <Button
                          color="primary"
                          size="sm"
                          disabled={!catalogWritable || !hasConcreteShop || busy[`auto-map-${selectedProduct.id}`]}
                          onClick={() => runAutoMap([selectedProduct.id])}
                        >
                          <IntlMessages id="shopee.workspace.mapping.auto-map-this" />
                        </Button>
                      ) : (
                        <div className="d-flex" style={{ gap: 8 }}>
                          {catalogWritable ? (
                            <Button
                              color="outline-primary"
                              size="sm"
                              onClick={() => openMapping(selectedProduct)}
                            >
                              <IntlMessages id="shopee.workspace.mapping.change" />
                            </Button>
                          ) : null}
                          <Button
                            color="outline-danger"
                            size="sm"
                            disabled={!catalogWritable || busy[`mapping-${selectedProduct.id}`]}
                            onClick={unmapSelectedProduct}
                          >
                            <IntlMessages id="shopee.workspace.mapping.remove" />
                          </Button>
                        </div>
                      )}
                    </div>
                  )}

                  {detailTab === "history" && (
                    <div style={{ fontSize: 11, color: "#64748b" }}>
                      <div className="mb-2"><strong><IntlMessages id="shopee.workspace.mapping.history-created" /></strong> {formatTimestamp(selectedProduct.createdAt)}</div>
                      <div className="mb-2"><strong><IntlMessages id="shopee.workspace.mapping.history-updated" /></strong> {formatTimestamp(selectedProduct.updatedAt)}</div>
                      <div className="mb-2"><strong><IntlMessages id="shopee.workspace.mapping.history-last-sync" /></strong> {formatTimestamp(selectedProduct.last_synced_at)}</div>
                    </div>
                  )}
                </div>

                {/* Sticky Action Button at bottom of Drawer */}
                <div className="mt-3 pt-2 border-top">
                  <Button
                    color="primary"
                    block
                    style={{ borderRadius: 6, fontWeight: 600, fontSize: 13 }}
                    disabled={
                      !selectedProduct.product_id ||
                      !inventoryWritable ||
                      !isConcreteShopeeShopId(selectedProduct.shopee.shopId) ||
                      busy[`sync-inventory-${selectedProduct.id}`]
                    }
                    onClick={syncSelectedInventory}
                  >
                    <IntlMessages id="shopee.workspace.mapping.sync-stock-to-shopee" />
                  </Button>
                </div>
              </CardBody>
            </Card>
          </div>
        )}
      </div>

      <Modal isOpen={Boolean(mappingDialog)} toggle={() => setMappingDialog(null)}>
        <ModalHeader toggle={() => setMappingDialog(null)}><IntlMessages id="shopee.workspace.mapping.modal-title" /></ModalHeader>
        <ModalBody>
          {mappingDialog ? (
            <Alert color="info" className="py-2">
              {mappingDialog.platform_name || mappingDialog.platform_sku || "Listing Shopee"}
              {mappingDialog.platform_model_id ? ` · Model ${mappingDialog.platform_model_id}` : ""}
            </Alert>
          ) : null}
          <Input
            className="mb-2"
            placeholder={intl.formatMessage({
              id: "shopee.workspace.mapping.candidate-search-placeholder"
            })}
            value={candidateSearch}
            onChange={event => setCandidateSearch(event.target.value)}
          />
          <Input
            type="select"
            value={productId}
            onChange={event => {
              setProductId(event.target.value);
              setVariantId("");
            }}
            disabled={candidatesLoading}
          >
            <option value="">
              {candidatesLoading
                ? intl.formatMessage({ id: "shopee.workspace.mapping.candidates-loading" })
                : intl.formatMessage({ id: "shopee.workspace.mapping.choose-product" })}
            </option>
            {candidates.map(candidate => (
              <option value={candidate.id} key={candidate.id}>
                {candidate.name || intl.formatMessage({ id: "shopee.workspace.mapping.nameless-product" })} · {candidate.sku || `#${candidate.id}`}
              </option>
            ))}
          </Input>
          {requiresVariant ? (
            <Input
              type="select"
              className="mt-2"
              value={variantId}
              onChange={event => setVariantId(event.target.value)}
              disabled={!productId || !availableVariants.length}
            >
              <option value="">{intl.formatMessage({ id: "shopee.workspace.mapping.choose-variant" })}</option>
              {availableVariants.map(variant => (
                <option value={variant.id} key={variant.id}>
                  {variant.name || variant.sku || `Variant #${variant.id}`}
                </option>
              ))}
            </Input>
          ) : null}
          {selectedCandidate &&
          mappingDialog &&
          String(mappingDialog.platform_model_id || "0") !== "0" &&
          !availableVariants.length ? (
            <div className="text-muted small mt-2">
              <IntlMessages id="shopee.workspace.mapping.no-variants-note" />
            </div>
          ) : null}
          {!candidatesLoading && mappingDialog && !candidates.length ? (
            <div className="text-muted small mt-2"><IntlMessages id="shopee.workspace.mapping.no-candidates" /></div>
          ) : null}
        </ModalBody>
        <ModalFooter>
          {mappingDialog && mappingDialog.product_id ? (
            <Button color="outline-danger" onClick={clearMapping} disabled={busy[`mapping-${mappingDialog.id}`]}>
              <IntlMessages id="shopee.workspace.catalog.unmap" />
            </Button>
          ) : null}
          {mappingDialog && canCreateShopeeProduct(mappingDialog, workspace.selectedShopId, catalogWritable) ? (
            <Button color="outline-success" onClick={() => { createProductFromShopee(mappingDialog); setMappingDialog(null); }}>
              <IntlMessages id="shopee.workspace.mapping.create-product" />
            </Button>
          ) : null}
          <Button color="secondary" onClick={() => setMappingDialog(null)}><IntlMessages id="shopee.cancel" /></Button>
          <Button
            color="primary"
            onClick={saveMapping}
            disabled={!Number(productId) || (requiresVariant && !Number(variantId)) || busy[`mapping-${mappingDialog && mappingDialog.id}`]}
          >
            <IntlMessages id="shopee.workspace.mapping.save" />
          </Button>
        </ModalFooter>
      </Modal>

    </div>
  );
});
